import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { Modal } from "@/components/Modals";
import * as orderApi from "@/lib/orderApi";

const serverReason = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail.length) return detail[0]?.msg || "Request rejected";
  return error?.message || "Could not reach the server";
};

const stepOf = line => (Number(line.quantity) >= 1 ? 1 : Number(line.quantity));

/**
 * Cancel Item: one line, some or all of its quantity — never the table.
 *
 * Floor report 2026-10-13: staff asked to cancel one item and the only cancel on
 * screen was Cancel Table. This voids `quantity` of `line` through
 * POST /orders/{id}/void-quantity; the rest of the order and the table are
 * untouched, and a sent line gets a VOID slip at its station. Nothing is sent
 * until staff confirm; success is announced only on the server's answer.
 *
 * `onDone(items)` receives the order's re-read lines.
 */
export default function CancelItemDialog({ orderId, line, token, onDone, onClose }) {
  const [qty, setQty] = useState(stepOf(line));
  const [reasons, setReasons] = useState(null);
  const [reasonId, setReasonId] = useState("");
  const [busy, setBusy] = useState(false);
  const step = stepOf(line);
  const max = Number(line.quantity);

  useEffect(() => {
    let alive = true;
    orderApi.listCancellationReasons(token)
      .then(rows => {
        if (!alive) return;
        const itemReasons = (rows || []).filter(r => r.applies_to !== "order" && r.is_active !== false);
        setReasons(itemReasons);
        setReasonId(itemReasons[0]?.id ?? "");
      })
      .catch(err => {
        if (!alive) return;
        setReasons([]);
        toast.error(`Could not load cancellation reasons: ${serverReason(err)}`);
      });
    return () => { alive = false; };
  }, [token]);

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    const amount = Math.min(qty, max);
    let result;
    try {
      // Re-read: a board row's version can be stale.
      const fresh = await orderApi.getOrder(orderId, token);
      result = await orderApi.voidQuantity(
        orderId, line.id, amount, reasonId ? Number(reasonId) : null, fresh.version, token
      );
    } catch (err) {
      toast.error(`Could not cancel ${line.item_name}: ${serverReason(err)}`);
      setBusy(false);
      return;
    }
    // The void is committed from here on: a failed re-read must not read as a
    // failed cancel, or a second tap would void another unit.
    toast.success(
      `Cancelled ${amount} × ${line.item_name}` +
        (line.kot_number ? " · VOID slip sent to the kitchen" : "") +
        (Number(result?.remaining_quantity) > 0 ? ` · ${result.remaining_quantity} still on the order` : "")
    );
    // The last live line went: the server ended the order and freed the table
    // (bible §39.2 "Fully cancelled"). This used to tell the captain to go and use
    // Cancel Table — a workaround the floor should never need (operating law 5).
    if (result?.order_cancelled) {
      toast.info(result.table_freed
        ? "Nothing left on this table — the order is closed and the table is free."
        : "Nothing left on this order — it is closed. The table stays with its other bill.");
      setBusy(false);
      onDone(null, { orderCancelled: true });
      return;
    }
    let items = null;
    try {
      items = await orderApi.getOrderItems(orderId, token);
    } catch (err) {
      toast.warning(`Cancelled, but the item list could not refresh: ${serverReason(err)}. Reopen the table to see it.`);
    }
    setBusy(false);
    onDone(items, { orderCancelled: false });
  };

  return (
    <Modal
      title={`Cancel item · ${line.item_name}`}
      onClose={() => { if (!busy) onClose(); }}
      testId="cancel-item-modal"
    >
      <div className="p-4 space-y-4">
        <p className="text-meta text-muted-foreground">
          Only this item is cancelled. The rest of the order and the table stay as they are
          {line.kot_number ? `, and the kitchen gets a VOID slip for KOT #${line.kot_number}` : ""}.
        </p>
        <div className="flex items-center justify-between">
          <span className="text-body font-bold">Quantity to cancel</span>
          <div className="flex items-center gap-3 bg-background border rounded-md">
            <button
              className="px-3 py-2 hover:bg-muted rounded-l-md disabled:opacity-40"
              onClick={() => setQty(q => Math.max(step, q - step))}
              disabled={busy || qty <= step}
              aria-label="Cancel fewer"
            ><Minus className="w-4 h-4" /></button>
            <span className="text-body font-bold w-14 text-center tabular-nums" data-testid="cancel-item-qty">{qty} / {max}</span>
            <button
              className="px-3 py-2 hover:bg-muted rounded-r-md disabled:opacity-40"
              onClick={() => setQty(q => Math.min(max, q + step))}
              disabled={busy || qty >= max}
              aria-label="Cancel more"
            ><Plus className="w-4 h-4" /></button>
          </div>
        </div>
        {reasons === null ? (
          <p className="text-meta text-muted-foreground">Loading reasons…</p>
        ) : reasons.length > 0 && (
          <label className="block">
            <span className="text-meta font-medium text-muted-foreground">Reason</span>
            <select
              className="mt-1 w-full h-11 rounded-md border bg-background px-3"
              value={reasonId}
              onChange={e => setReasonId(e.target.value)}
              disabled={busy}
              data-testid="cancel-item-reason"
            >
              {reasons.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <button
            className="py-3 rounded-md font-bold border hover:bg-muted disabled:opacity-50"
            onClick={onClose}
            disabled={busy}
          >Keep item</button>
          <button
            className="py-3 rounded-md font-bold bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            onClick={confirm}
            disabled={busy || reasons === null}
            data-testid="confirm-cancel-item"
          >{busy ? "Cancelling…" : `Cancel ${qty} × item`}</button>
        </div>
      </div>
    </Modal>
  );
}
